import React from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Tooltip, IconButton, Divider } from '@mui/material';
import { SwapHoriz as SwapIcon } from '@mui/icons-material';
import type { CaboTipo, PadraoTuboLoose } from '../../../services/caboService';
import { getPadraoTuboLooseLabel } from '../../../services/caboService';
import { getCorFibraNoCabo, getFibrasPorTubo, getTubosLoose } from '../../../utils/tuboLooseUtils';
import { useDiagramInteractive } from '../../../utils/useDiagramInteractive';

export interface CaboFiberGroupsProps {
  nodeId: string;
  splitterId: number;
  qtde_fo: number;
  qtde_tubo_loose?: number;
  padrao_tubo_loose?: PadraoTuboLoose;
  cabo_tipo?: CaboTipo | null;
  portas_tipo: Record<string, 'in' | 'out'>;
  defaultPortaTipo: 'in' | 'out';
  handleColor: string;
  portasComFusao: Set<number>;
  isHandleConnected: (handleId: string, handleType: 'source' | 'target') => boolean;
  onUpdatePortaTipo?: (splitterId: number, numeroFibra: number, tipo: 'in' | 'out') => void;
  handlePosition?: Position.Left | Position.Right;
  opticalSignal?: {
    inputSignal: number | null;
    outputSignals: Record<string, number>;
  };
}

const CaboFiberGroups: React.FC<CaboFiberGroupsProps> = ({
  nodeId,
  splitterId,
  qtde_fo,
  qtde_tubo_loose = 1,
  padrao_tubo_loose = 'abnt',
  cabo_tipo,
  portas_tipo,
  defaultPortaTipo,
  handleColor,
  portasComFusao,
  isHandleConnected,
  onUpdatePortaTipo,
  handlePosition = Position.Right,
  opticalSignal,
}) => {
  const padrao = padrao_tubo_loose;
  const tubosQtd = cabo_tipo === 'asu' ? 1 : qtde_tubo_loose;
  const fibrasPorTubo = getFibrasPorTubo(qtde_fo, tubosQtd, cabo_tipo);
  const tubos = getTubosLoose(qtde_fo, tubosQtd, padrao, cabo_tipo);
  const scrollArea = qtde_fo > 48 || tubos.length > 6;
  const isLeft = handlePosition === Position.Left;
  const isDiagramInteractive = useDiagramInteractive();

  const renderFibraRow = (numeroFibra: number, fibrasNoTubo: number) => {
    const corFibra = getCorFibraNoCabo(numeroFibra, padrao, fibrasNoTubo);
    const tipoPorta = portas_tipo[String(numeroFibra)] ?? defaultPortaTipo;
    const sourceId = `output-fo${numeroFibra}`;
    const targetId = `input-fo${numeroFibra}`;
    const showOut = tipoPorta === 'out';
    const showIn = !showOut;

    const handleStyle = {
      background: corFibra.cor,
      border: corFibra.nome === 'Branco' ? '2px solid #ccc' : '2px solid #fff',
      width: 12,
      height: 12,
      position: 'absolute' as const,
      top: '50%',
      transform: 'translateY(-50%)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      flexShrink: 0,
      zIndex: 2,
    };
    const hiddenHandleStyle = {
      ...handleStyle,
      opacity: 0,
      pointerEvents: 'none' as const,
    };

    const signalLabelOut = opticalSignal?.outputSignals?.[sourceId] !== undefined
      ? `${opticalSignal.outputSignals[sourceId].toFixed(2)} dBm`
      : null;

    const handles = (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          ...(isLeft ? { left: -8 } : { right: -8 }),
          width: 12,
          height: 12,
          zIndex: 2,
        }}
      >
        <Handle
          type="target"
          position={handlePosition}
          id={targetId}
          isConnectable={showIn && !isHandleConnected(targetId, 'target')}
          style={{
            ...(showIn ? handleStyle : hiddenHandleStyle),
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'none',
          }}
        />
        <Handle
          type="source"
          position={handlePosition}
          id={sourceId}
          isConnectable={showOut && !isHandleConnected(sourceId, 'source')}
          style={{
            ...(showOut ? handleStyle : hiddenHandleStyle),
            position: 'absolute',
            left: 0,
            top: 0,
            transform: 'none',
          }}
        />
      </Box>
    );

    const fiberInfo = (
      <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#555', minWidth: 22 }}>
        FO{numeroFibra}
      </Typography>
    );

    return (
      <Box
        key={`${nodeId}-fo${numeroFibra}`}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 20,
          py: 0.2,
          pl: isLeft ? 1.5 : 0.4,
          pr: isLeft ? 0.4 : 1.5,
          gap: 0.4,
          overflow: 'visible',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 0.5 },
        }}
      >
        {handles}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flex: 1, minWidth: 0 }}>
          {fiberInfo}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
          {signalLabelOut && showOut && (
            <Typography sx={{ fontSize: '0.52rem', fontWeight: 'bold', color: '#4caf50' }}>
              {signalLabelOut}
            </Typography>
          )}
          {onUpdatePortaTipo && isDiagramInteractive && (
            <Tooltip title={tipoPorta === 'out' ? 'Alterar para entrada' : 'Alterar para saída'} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdatePortaTipo(splitterId, numeroFibra, tipoPorta === 'out' ? 'in' : 'out');
                }}
                sx={{
                  width: 16,
                  height: 16,
                  color: handleColor,
                  p: 0,
                  ...(portasComFusao.has(numeroFibra) ? { visibility: 'hidden', pointerEvents: 'none' } : {}),
                }}
              >
                <SwapIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        mt: 0.5,
        ...(scrollArea
          ? {
              maxHeight: 420,
              overflowY: 'auto',
              overflowX: 'visible',
              ...(isLeft ? { pl: 0.5 } : { pr: 0.5 }),
              '&::-webkit-scrollbar': { width: 5 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 },
            }
          : { overflow: 'visible' }),
      }}
    >
      <Typography sx={{ fontSize: '0.6rem', color: '#666', textAlign: 'center', mb: 0.4 }}>
        {qtde_fo} FO • {tubos.length} tubo{tubos.length > 1 ? 's' : ''} loose • {fibrasPorTubo} FO/tubo • {getPadraoTuboLooseLabel(padrao)}
        {cabo_tipo === 'asu' ? ' • ASU' : ''}
      </Typography>

      {tubos.map((tubo, idx) => (
        <Box key={`tubo-${tubo.tuboNum}`} sx={{ mb: idx < tubos.length - 1 ? 0.6 : 0 }}>
          {idx > 0 && <Divider sx={{ mb: 0.5 }} />}
          <Box
            sx={{
              border: `1.5px solid ${tubo.corTubo.cor}88`,
              borderRadius: 1,
              overflow: 'visible',
              backgroundColor: 'rgba(255,255,255,0.7)',
            }}
          >
            <Box
              sx={{
                px: 0.7,
                py: 0.3,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: `${tubo.corTubo.cor}22`,
                borderBottom: `1px solid ${tubo.corTubo.cor}55`,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: tubo.corTubo.cor,
                  border: tubo.corTubo.nome === 'Branco' ? '1px solid #ccc' : 'none',
                }}
              />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#444' }}>
                Tubo {tubo.tuboNum}
              </Typography>
              <Typography sx={{ fontSize: '0.55rem', color: '#666' }}>
                {tubo.corTubo.nome} • FO {tubo.inicio}–{tubo.fim}
              </Typography>
            </Box>
            <Box sx={{ px: 0.3, py: 0.2, overflow: 'visible' }}>
              {tubo.fibras.map((n) => renderFibraRow(n, tubo.fibrasPorTubo))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default CaboFiberGroups;
